import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';
import SafetyEvolucaoCharts from './SafetyEvolucaoCharts';
import {
  NearMissDistributionCharts,
  SectorCompareBarChart,
} from './SafetyDistributionCharts';
import SafetyRecurrencePanel from './SafetyRecurrencePanel';

export default function SafetyHuddleOverviewDashboardPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await shJson('/api/safety-huddle/dashboard/overview?months=12', unidadeSelecionada);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const t = data?.totals || {};

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main">
        Dashboard Safety Huddle
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Evolução de formulários, setores e itens
        {data?.seeAll ? ' · visão completa da unidade' : ' · filtrado pelo seu setor'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip color="primary" label={`${t.sectors || 0} setores`} />
        <Chip color="primary" variant="outlined" label={`${t.templates || 0} formulários`} />
        <Chip label={`${t.questions || 0} perguntas`} />
        <Chip label={`${t.submissions || 0} envios`} />
        <Chip color="warning" label={`${t.nearMisses || 0} quase-falhas`} />
      </Box>

      <SafetyEvolucaoCharts data={data?.evolucao || []} title="Últimos 12 meses" />

      <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Por setor
      </Typography>
      <SectorCompareBarChart bySector={data?.bySector || []} />
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {(data?.bySector || []).map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.sectorId}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(25,118,210,0.12)',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => navigate(`/safety-huddle/setores/${s.sectorId}`)}
            >
              <Typography fontWeight={700}>{s.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {s.forms} formulário(s) · {s.envios} envios · {s.quaseFalhas} quase-falhas
              </Typography>
            </Paper>
          </Grid>
        ))}
        {!data?.bySector?.length && <Alert severity="info">Nenhum setor no seu escopo.</Alert>}
      </Grid>

      <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
        Quase-falhas
      </Typography>
      <Box sx={{ mb: 3 }}>
        <NearMissDistributionCharts
          bySeverity={data?.nearMissBySeverity}
          byStatus={data?.nearMissByStatus}
        />
      </Box>

      <SafetyRecurrencePanel recurrence={data?.recurrence} mode="overview" />

      <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
        Formulários
      </Typography>
      <List>
        {(data?.templates || []).map((tpl) => (
          <ListItemButton
            key={tpl.id}
            sx={{ borderRadius: 2, border: '1px solid rgba(25,118,210,0.1)', mb: 1 }}
            onClick={() =>
              navigate(`/safety-huddle/setores/${tpl.sectorId}/dashboard/${tpl.id}`)
            }
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography fontWeight={700}>{tpl.name}</Typography>
                  <Chip size="small" label={tpl.sectorName} />
                  <Chip size="small" variant="outlined" label={`${tpl.submissionsCount} envios`} />
                  <Chip size="small" variant="outlined" label={`${tpl.questionsCount} perguntas`} />
                </Box>
              }
            />
          </ListItemButton>
        ))}
        {!data?.templates?.length && <Alert severity="info">Nenhum formulário ativo.</Alert>}
      </List>

      <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
        Itens (perguntas) — volume de respostas
      </Typography>
      <List dense>
        {(data?.questionStats || []).map((q) => (
          <ListItemButton
            key={q.questionId}
            sx={{ borderRadius: 1, mb: 0.5 }}
            onClick={() =>
              navigate(`/safety-huddle/setores/${data.templates.find((t) => t.id === q.templateId)?.sectorId}/dashboard/${q.templateId}`)
            }
          >
            <ListItemText
              primary={q.label}
              secondary={`${q.sectorName} · ${q.templateName} · ${q.answers} respostas · ${q.responseType}`}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}
